/**
 * Turns a request into a human phrase ("save the listing") so an error message
 * can name the operation that failed even when nothing is known about the
 * cause. This is what keeps messages specific instead of vague: a 500 with an
 * unreadable backend exception still produces "We couldn't save the listing".
 */

const VERBS = {
  get: "load",
  post: "create",
  put: "save",
  patch: "save",
  delete: "delete",
};

// Present participles, written out rather than derived — "save" + "ing" is
// "saveing", and every rule for fixing that has more exceptions than entries.
const GERUNDS = {
  load: "Loading",
  create: "Creating",
  save: "Saving",
  delete: "Deleting",
  complete: "Completing",
};

// Path segment → noun. Only where the humanised segment would read badly or
// ambiguously; everything else falls through to the humaniser below.
const NOUNS = {
  "quote-card": "quote card",
  px: "part exchange",
  pnl: "profit report",
  log: "activity log",
  ai: "AI assistant",
  files: "file",
  pdfs: "file",
};

const humanise = (segment) =>
  segment
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    // "categories" → "category"; "listings" → "listing"; "address" is left alone.
    .replace(/(\w)ies$/, "$1y")
    .replace(/([^s])s$/, "$1");

const pathOf = (url) => {
  if (!url) return "";
  const withoutQuery = String(url).split("?")[0];
  return withoutQuery.replace(/^https?:\/\/[^/]+/i, "");
};

const nounOf = (url) => {
  const segments = pathOf(url)
    .split("/")
    .filter((segment) => segment && segment !== "api");
  const first = segments[0];
  if (!first) return null;
  return NOUNS[first] ?? humanise(first) ?? null;
};

export function describeOperation(method, url) {
  const verb = VERBS[String(method ?? "").toLowerCase()] ?? "complete";
  const noun = nounOf(url);

  if (!noun) {
    return {
      verb: "complete",
      gerund: GERUNDS.complete,
      noun: "that",
      phrase: "complete that",
    };
  }

  return {
    verb,
    gerund: GERUNDS[verb] ?? GERUNDS.complete,
    noun,
    phrase: `${verb} the ${noun}`,
  };
}
